import {
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts';
import { CustomReferenceDot } from './custom-reference-dot';
import { Defs } from './defs';
import { CustomTooltip } from './custom-tooltip';
import { ClimaDia } from '../../service/clima';
import { nearDots } from '../../utils/functions/nearDots';

export const axis = {
  tickLine: false,
  axisLine: false,
  tick: { fontSize: 15, fill: '#333', fontWeight: '500' } as const,
  tickMargin: 7,
};

type Props = {
  weather: ClimaDia | null;
  syncId: string;
  tomorrow?: boolean;
  globalMaxWind: number;
};

type Key = 'AGORA' | 'MÁX' | 'MÍN';

export function WindSpeed({ weather, syncId, tomorrow, globalMaxWind }: Props) {
  const chartData =
    weather?.por_hora?.map((h) => ({
      hour: h.hora,
      wind: h.vento,
    })) ?? [];

  const hasData = chartData.length > 0;

  const winds = hasData ? chartData.map((d) => d.wind) : [0];
  const windMax = Math.max(...winds);
  const windMin = Math.min(...winds);

  const pontoMax = hasData
    ? chartData.find((d) => d.wind === windMax)
    : undefined;

  const pontoMin = hasData
    ? chartData.find((d) => d.wind === windMin)
    : undefined;

  const timeZone = 'America/Sao_Paulo';
  const now = new Date();
  const currentHourNum = Number(
    new Intl.DateTimeFormat('pt-BR', {
      hour: '2-digit',
      hour12: false,
      timeZone,
    }).format(now)
  );

  const hourToNum = (h: string) => Number(h.slice(0, 2));

  let pontoAtual = hasData
    ? chartData.find((d) => hourToNum(d.hour) === currentHourNum)
    : undefined;

  if (!pontoAtual && hasData) {
    const passados = chartData.filter(
      (d) => hourToNum(d.hour) <= currentHourNum
    );
    pontoAtual = passados.length ? passados[passados.length - 1] : chartData[0];
  }

  const maxYBase = globalMaxWind;
  const maxY = Math.ceil((maxYBase + 2) / 5) * 5;

  const yTicks = Array.from(
    { length: Math.floor(maxY / 5) + 1 },
    (_, i) => i * 5
  );

  const offsets: Record<Key, { dx: number; dy: number }> = {
    AGORA: { dx: 0, dy: 0 },
    MÁX: { dx: 0, dy: 0 },
    MÍN: { dx: 0, dy: 0 },
  };

  if (nearDots(pontoAtual, pontoMax, 'wind')) {
    offsets.MÁX.dy = 30;
    offsets.MÁX.dx = -5;
    offsets.AGORA.dx = 10;
  }

  if (nearDots(pontoAtual, pontoMin, 'wind')) {
    offsets.MÍN.dy = 30;
    offsets.MÍN.dx = -5;
    offsets.AGORA.dx = 10;
  }

  return (
    <ResponsiveContainer width="100%" height={300}>
      <AreaChart
        width={500}
        data={chartData}
        syncId={syncId}
        margin={{ right: 8, left: 13 }}
      >
        <Defs />

        <CartesianGrid stroke="#e0e0e0" strokeDasharray="5 5" />

        <XAxis dataKey="hour" ticks={chartData.map((d) => d.hour)} {...axis} />
        <YAxis
          orientation="right"
          domain={[0, maxY]}
          ticks={yTicks}
          tickFormatter={(v: number) => `${v} km/h`}
          {...axis}
        />

        <CustomTooltip
          formatter={(value) => [`${Math.round(Number(value))} km/h`, 'Vento']}
        />

        <Area
          type="monotone"
          dataKey="wind"
          stroke="url(#lineGradient)"
          strokeWidth={4}
          fill="url(#areaGradient)"
          dot={false}
          cursor="pointer"
        />

        {pontoMax && (
          <CustomReferenceDot
            x={pontoMax.hour}
            y={pontoMax.wind}
            value="MÁX"
            dx={offsets.MÁX.dx}
            dy={offsets.MÁX.dy}
          />
        )}

        {pontoMin && (
          <CustomReferenceDot
            x={pontoMin.hour}
            y={pontoMin.wind}
            value="MÍN"
            dx={offsets.MÍN.dx}
            dy={offsets.MÍN.dy}
          />
        )}

        {pontoAtual && !tomorrow && (
          <CustomReferenceDot
            x={pontoAtual.hour}
            y={pontoAtual.wind}
            value="AGORA"
            dx={offsets.AGORA.dx}
            dy={offsets.AGORA.dy}
          />
        )}
      </AreaChart>
    </ResponsiveContainer>
  );
}
